import {
  AppointmentManageTargetDto,
  CreateBookingDto,
  OwnerBookingDto,
  OwnerBookingWithTypeDto,
  ProviderDetailsDto,
  PublicBookingDto,
  PublicBookingFields,
  RescheduleBookingDto,
} from '../core/bookings';
import { HttpClient } from './http';
import { catch404ReturnNull } from './util';

export interface BookingsService {
  getProviderDetails: (providerId: string) => Promise<ProviderDetailsDto>;
  getBookingById: (bookingId: string) => Promise<PublicBookingDto | null>;
  getManageTarget: (bookingId: string) => Promise<AppointmentManageTargetDto>;
  createBooking: (dto: CreateBookingDto) => Promise<PublicBookingFields>;
  rescheduleBooking: (
    dto: RescheduleBookingDto,
  ) => Promise<PublicBookingFields>;
  /** Owner-authenticated, so it carries the client's email. Public reads do not. */
  getUpcomingBookings: (limit?: number) => Promise<OwnerBookingDto[]>;
  /** Owner-authenticated; `eventId` is the `evn_` row id, never the Google event id. */
  getBookingByEventId: (
    eventId: string,
  ) => Promise<OwnerBookingWithTypeDto | null>;
}

export function createBookingsService(
  httpClient: HttpClient,
  baseUrl: string,
): BookingsService {
  return {
    getProviderDetails: (providerId: string) => {
      return httpClient<ProviderDetailsDto>(
        `${baseUrl}/bookings/${providerId}/provider-details`,
        'get',
      );
    },
    getBookingById: (bookingId: string) => {
      return httpClient<PublicBookingDto | null>(
        `${baseUrl}/bookings/${bookingId}`,
        'get',
      );
    },
    getManageTarget: (bookingId: string) => {
      return httpClient<AppointmentManageTargetDto>(
        `${baseUrl}/appointments/${encodeURIComponent(bookingId)}/manage-target`,
        'get',
      );
    },
    createBooking: (dto: CreateBookingDto) => {
      return httpClient<PublicBookingFields>(`${baseUrl}/bookings`, 'post', {
        body: dto,
      });
    },
    rescheduleBooking: (dto: RescheduleBookingDto) => {
      return httpClient<PublicBookingFields>(
        `${baseUrl}/bookings/reschedule`,
        'post',
        {
          body: dto,
        },
      );
    },
    getUpcomingBookings: (limit?: number) => {
      const params = limit !== undefined ? `?limit=${limit}` : '';
      return httpClient<OwnerBookingDto[]>(
        `${baseUrl}/bookings/upcoming${params}`,
        'get',
      );
    },
    getBookingByEventId: (eventId: string) =>
      catch404ReturnNull(() =>
        httpClient<OwnerBookingWithTypeDto>(
          `${baseUrl}/bookings/by-event/${encodeURIComponent(eventId)}`,
          'get',
        ),
      ),
  };
}
